import { useMemo } from 'react';
import { TourStatus, type TourSummaryItem } from '../store/facade/facadeApiV3';
import first from 'lodash/fp/first';

export const OFF_SET_THRESHOLD = 10; // minutes

const isNotInProgress = (tour: TourSummaryItem) => tour.status !== TourStatus.InProgress;
const hasDepartureDate = (tour: TourSummaryItem) => !!tour.itinerarySummary?.departureEvent?.estimatedDay;

const MAX_TOURS_PER_LIST = 20;

const getDepartureTime = (tour: TourSummaryItem) =>
    new Date(tour.itinerarySummary?.departureEvent?.estimatedDay ?? '').getTime();

export const useCategorizedTours = (tours: TourSummaryItem[] | undefined) => {
    return useMemo(() => {
        const allTours = tours ?? [];
        const now = new Date();
        const thresholdTime = new Date(now.getTime() - OFF_SET_THRESHOLD * 60 * 1000);

        const futureTours = allTours
            .filter(
                tour =>
                    isNotInProgress(tour) &&
                    hasDepartureDate(tour) &&
                    new Date(tour.itinerarySummary?.departureEvent?.estimatedDay ?? '') >= thresholdTime
            )
            .sort((a, b) => getDepartureTime(a) - getDepartureTime(b));

        const pastTours = allTours
            .filter(
                tour =>
                    isNotInProgress(tour) &&
                    hasDepartureDate(tour) &&
                    new Date(tour.itinerarySummary?.departureEvent?.estimatedDay ?? '') < thresholdTime
            )
            .sort((a, b) => getDepartureTime(b) - getDepartureTime(a))
            .slice(0, MAX_TOURS_PER_LIST);

        const inProgressTours = allTours.filter(tour => tour.status === TourStatus.InProgress);
        const onGoingTour =
            first(inProgressTours.filter(hasDepartureDate).sort((a, b) => getDepartureTime(b) - getDepartureTime(a))) ??
            inProgressTours[0];

        return {
            onGoingTour,
            futureTours,
            pastTours,
        };
    }, [tours]);
};
